import type { ComponentProps } from "react";
import { useCallback, useState } from "react";

import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Stack } from "@similarweb/ui-components/foundations";

import { ADDON_TYPE_NAME_DICT } from "app/contexts/AddonStateContext/const";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { DEFAULT_PAGE_SIZE } from "./constants";
import { TopicsEnrichedContainer } from "./enrichTable/TopicsEnrichedContainer";
import { getTopicsTableColumns } from "./topicsTableColumnsConfig";
import type { ITopicsTable, ITransformedTopicsData } from "./types";
import { EnrichedTabs } from "./types";

export const TopicsTable = (props: ITopicsTable) => {
    const {
        data = [],
        isLoading,
        isError,
        handleSort,
        sortConfig,
        totalCount,
        currentPage,
        onPageChange,
        excelUrl,
        requestBodyForExcel,
        selectedPageTab,
        showKeywordGroupButton,
    } = props;

    const { hasAddonsHub } = useAddonsState();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const [expandedRowKey, setExpandedRowKey] = useState<number[]>([]);
    const [expandedRowTabId, setExpandedRowTabId] = useState<EnrichedTabs>(EnrichedTabs.Keywords);

    const toggleExpandRow = useCallback(
        (key: number, tabId: EnrichedTabs) => {
            const isSameRow = expandedRowKey[0] === key;
            const isSameTab = expandedRowTabId === tabId;

            if (isSameRow && isSameTab) {
                setExpandedRowKey([]);
                return;
            }

            setExpandedRowTabId(tabId);
            setExpandedRowKey([key]);
        },
        [expandedRowKey, expandedRowTabId],
    );

    const onPageChangeHandler = (newPage: number) => {
        onPageChange(newPage);
    };

    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "click", "Table/Excel");
    };

    const noDataObject = isError
        ? {
              title: translate("website.topics.table.error.title"),
              subtitle: translate("website.topics.table.error.subtitle"),
          }
        : {
              title: translate("website.topics.table.no.data.title"),
              subtitle: translate("website.topics.table.no.data.subtitle"),
          };

    const onSort: ComponentProps<typeof Table<ITransformedTopicsData>>["onSort"] = (sorter) => {
        const isAsc = sorter.order === "ascend";
        handleSort({
            sort: sorter.columnKey as keyof ITransformedTopicsData,
            asc: isAsc,
        });
        onPageChange(1);
    };

    const columns = getTopicsTableColumns({
        currentPage,
        pageSize: DEFAULT_PAGE_SIZE,
        selectedPageTab,
        toggleExpandRow,
        sortConfig,
        totalCount,
    });

    return (
        <Stack
            boxSizing="border-box"
            background={colorsPalettes.carbon[0]}
        >
            <Table<ITransformedTopicsData>
                columnConfig={columns}
                data={data}
                isLoading={isLoading}
                loaderRowCount={DEFAULT_PAGE_SIZE / 2}
                noData={noDataObject}
                size="large"
                pagination={{
                    pageSize: DEFAULT_PAGE_SIZE,
                    total: totalCount ?? 0,
                    current: currentPage,
                    onChange: (_, __, newPage) => onPageChangeHandler(newPage),
                }}
                sticky={false}
                expandable={{
                    expandedRowRender: (record: ITransformedTopicsData) => (
                        <TopicsEnrichedContainer
                            row={record}
                            onClose={() => setExpandedRowKey([])}
                            expandedRowTabId={expandedRowTabId}
                            showKeywordGroupButton={showKeywordGroupButton}
                        />
                    ),
                    expandIcon: () => null,
                    expandedRowKeys: expandedRowKey,
                    onExpand: (_, record) => toggleExpandRow(record.key, expandedRowTabId),
                    expandIconColumnIndex: -1,
                    expandedRowClassName: (record) =>
                        expandedRowKey.length > 0 && expandedRowKey[0] === record.key ? "expanded-row-active" : "",
                }}
                onSort={onSort}
            >
                <Table.TableTop borderBottom>
                    <Table.TableTop.Settings
                        locale={{
                            columnPicker: {
                                mandatoryField: translate("keyword.analysis.table.column.picker.mandatory"),
                            },
                        }}
                        excelExportConfig={{
                            excelDownloadUrl: excelUrl,
                            excelBodyForReq: requestBodyForExcel,
                            hasAddonHub: hasAddonsHub,
                            onExcelDownloaded: trackExcelDownload,
                            onCtaClicked: () => handlePurchaseAddonClick(ADDON_TYPE_NAME_DICT.DataCreditsDynamic),
                        }}
                        columnPickerType="reorderable"
                    />
                </Table.TableTop>
            </Table>
        </Stack>
    );
};
